/**
 * The three things you can start, on the page that lists what you started
 * (#2158 slice 3a).
 *
 * **Why a bar and not the runner page's picker.** That picker shows three
 * sections of templates and asks the operator to choose one before anything
 * happens. The common case is not a choice: it is "run the tests I already
 * have, the way I already set them up". So the saved settings are resolved
 * behind the scenes and named in a line underneath, where they can be changed
 * but are not in the way.
 *
 * **Each button uses its own kind's default.** A single default across all
 * kinds — which is what the runner page does — would let all three buttons
 * start the same kind of run. The caller resolves per kind and passes the ids
 * in; a kind with no saved settings simply has no button, because a control
 * that cannot work is worse than an absent one.
 *
 * **The buttons open the settings, they do not start (#2175 slice 2).**
 * Pressing one and having a run begin with no confirmation gave the operator
 * nowhere to see, let alone change, what it was about to do. Each now opens
 * its own saved settings, which is also where the run is started from. One
 * word per button, with the sentence in a hover hint — three descriptive
 * labels in a row read as a paragraph and stopped looking like buttons.
 *
 * That is also why there is no "Change run settings" link any more (#2175
 * slice 5). It pointed at a page holding every pool's settings at once,
 * which is the thing this meta removed; and with the buttons opening the
 * settings themselves, a separate way in would be a second door to the
 * same room.
 */
import { Link } from 'react-router-dom'
import { Play, RotateCcw, Compass } from 'lucide-react'
import { Button } from '@/components/ui'
import { describePhases } from '@/lib/runPhases'
import type { RunTemplateKind, UnifiedRunTemplate } from '@/types'

export interface RunStartBarProps {
  /** The saved settings per kind. A missing key means that kind has no
   * settings for this variant, and its button is not rendered.
   *
   * The whole row, not just its id (#2225): each button says which stages
   * it will run, read from the template itself so an edited one tells the
   * truth. The caller already had the rows and was reducing them to ids.
   *
   * `Partial<Record<…>>` rather than `Record<string, …>`, matching what
   * `useManualRunTemplates` already returns: indexing yields `undefined`
   * for a kind with no settings, which is the case the guard below exists
   * for. The wider type made that guard read as dead code (Copilot review
   * on PR #2227). */
  defaultsByKind: Partial<Record<RunTemplateKind, UnifiedRunTemplate>>
  /** Disabled while a run is starting or in flight — one at a time. */
  busy: boolean
  /** Open that kind's saved settings. The bar does not start the run —
   * the dialog it opens does, once the operator has seen what will run. */
  onStart: (kind: 'generation' | 'resume' | 'replay') => void
  /** Where the empty state sends someone whose variant has no settings at
   * all, as a router path handed to `Link` — so navigation stays
   * client-side. It renders as an `href`, but a plain anchor would
   * full-page reload and drop the in-memory app and variant selection
   * (Copilot review on PR #2168, wording on #2169). */
  settingsHref: string
}

export function RunStartBar({
  defaultsByKind,
  busy,
  onStart,
  settingsHref,
}: RunStartBarProps) {
  const any = Object.keys(defaultsByKind).length > 0
  if (!any) {
    return (
      <div className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
        This variant has no run settings, so there is nothing to run with. Settings
        are created with the variant, so this usually means the variant needs
        another look.{' '}
        <Link to={settingsHref} className="text-primary underline underline-offset-2">Open Apps</Link>.
      </div>
    )
  }

  /** Button plus the stages it will run. The line is muted and small: it
   * answers a question you have once, and must not compete with the label
   * you read every time. */
  const start = (
    kind: RunTemplateKind,
    hint: string,
    icon: React.ReactNode,
    label: string,
    variant?: 'outline',
  ) => {
    const template = defaultsByKind[kind]
    if (!template) return null
    const stages = describePhases(template)
    return (
      <span title={hint} className="flex flex-col">
        <Button variant={variant} size="sm" disabled={busy} onClick={() => onStart(kind)}>
          {icon}
          {label}
        </Button>
        {stages && (
          <span className="mt-1 text-[10px] text-muted-foreground text-center">{stages}</span>
        )}
      </span>
    )
  }

  return (
    <div className="rounded-lg border bg-muted/30 px-4 py-3 flex items-center gap-3 flex-wrap">
      <div className="flex items-start gap-2 flex-wrap">
        {start('replay', 'Run tests or test suites you already have',
          <Play className="h-3.5 w-3.5 mr-1.5" />, 'Run Tests')}
        {start('generation', 'Create tests from what you picked on the Proposed tab, or from scratch',
          <Compass className="h-3.5 w-3.5 mr-1.5" />, 'Create Tests', 'outline')}
        {start('resume', 'Finish tests that were started but never completed',
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />, 'Resume Tests', 'outline')}
      </div>
    </div>
  )
}
